import React, { useState, useMemo, useEffect, useCallback } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
  type ColumnDef,
  type Table as TanStackTable,
  type PaginationState,
  type Row,
  type Cell,
  type Column,
  type CellContext,
  type FilterFnOption,
} from "@tanstack/react-table";

// UI Components
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./components/ui/table";
import { Skeleton } from "./components/ui/skeleton";
import { Button } from "./components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
} from "./components/ui/dropdown-menu";
import {
  TooltipProvider,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "./components/ui/tooltip";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectValue,
  SelectItem,
} from "./components/ui/select";

// Icons
import {
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  ChevronLeft,
  ChevronsLeft,
  ChevronRight,
  ChevronsRight,
  Download,
  RotateCw,
  AlertCircle,
} from "lucide-react";
import { DateRangeFilter } from "./components/DateRangeFilter";
import {
  ViewSelector,
  SaveViewDialog,
  DeleteViewDialog,
} from "./components/Views";
import { TablePagination } from "./components/TablePagination";
import { ColumnVisibilityToggle } from "./components/ColumnVisibilityToggle";
import { GlobalFilter } from "./components/GlobalFilter";
import { ColumnFilter } from "./components/ColumnFilter";
import { EditableCellWithFormatting } from "./components/EditableCellWithFormatting";
import { SubRowsTable } from "./components/SubRowsTable";
import { ExportProgressModal } from "./components/ExportProgressModal";

import { useServerSideTable } from "./hooks/useServerSideTable";
import { useBatchedExport } from "./hooks/useBatchedExport";
import {
  ColumnConfig,
  ViewState,
  EnhancedDataTableProps,
  TableTheme,
  tableThemes,
  DEFAULT_BATCHED_EXPORT_CONFIG,
} from "./types";

export function EnhancedDataTable<T>({
  data = [],
  columns,
  loading = false,
  skeletonRows = 5,
  emptyMessage = "No items found",
  onRowClick,
  enableGlobalSearch = false,
  enableColumnVisibility = false,
  enablePagination = false,
  enableCSVExport = false,
  batchedExportConfig = { enabled: false },
  enableDateRangeFilter = false,
  dateFilterField,
  enableCustomDateRangeFilter = false,
  globalSearchPlaceholder = "Search all columns...",
  initialPageSize = 50,
  pageSizeOptions = [10, 20, 30, 50, 100],
  enableCustomViews = false,
  viewStorageKey = "table-views",
  onViewChange,
  hasSubRows = false,
  getSubRows,
  subRowsColumns = [],
  expandedItems = new Set(),
  onToggleExpand,
  getRowId,
  theme = "light",
  customTheme,
  serverSide,
}: EnhancedDataTableProps<T>) {
  // Server-side hook
  const serverSideTable = useServerSideTable(
    serverSide || {
      enabled: false,
      onDataRequest: async () => ({
        success: false,
        message: "",
        data: [],
        meta: { total: 0, page: 1, limit: 10, totalPages: 0 },
      }),
    },
    initialPageSize
  );

  const [globalFilter, setGlobalFilter] = useState("");
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: initialPageSize,
  });
  const [customViews, setCustomViews] = useState<ViewState[]>([]);
  const [currentView, setCurrentView] = useState<ViewState | null>(null);
  const [columnVisibility, setColumnVisibility] = useState<
    Record<string, boolean>
  >({});
  const [viewToDelete, setViewToDelete] = useState<ViewState | null>(null);
  const [dateRange, setDateRange] = useState("all");
  const [filteredData, setFilteredData] = useState(data);
  const [isExporting, setIsExporting] = useState(false);
  const [showProgressModal, setShowProgressModal] = useState(false);

  // Determine if we're in server-side mode
  const isServerSide = serverSide?.enabled || false;

  const batchedExport = useBatchedExport(
    serverSide,
    batchedExportConfig || DEFAULT_BATCHED_EXPORT_CONFIG
  );

  // Use appropriate data source
  const tableData = isServerSide ? serverSideTable.data : filteredData;
  const isLoading = isServerSide ? serverSideTable.loading : loading;
  const currentError = isServerSide ? serverSideTable.error : null;

  const resolvedTheme = useMemo(() => {
    let baseTheme: TableTheme;

    if (typeof theme === "string") {
      baseTheme = tableThemes[theme];
    } else {
      baseTheme = theme;
    }

    // Merge with custom theme if provided
    return customTheme ? { ...baseTheme, ...customTheme } : baseTheme;
  }, [theme, customTheme]);

  const exportCurrentPage = async () => {
    try {
      setIsExporting(true);
      const visibleColumns = table
        .getVisibleFlatColumns()
        .filter((col) => col.id !== "select");
      const rows = table.getRowModel().rows; // Current page only

      await exportRows(rows, visibleColumns, "current-page");
    } catch (error) {
      console.error("Export current page error:", error);
    } finally {
      setIsExporting(false);
    }
  };

  // const exportAllFiltered = async () => {
  //   try {
  //     setIsExporting(true);
  //     const visibleColumns = table
  //       .getVisibleFlatColumns()
  //       .filter((col) => col.id !== "select");

  //     if (isServerSide && serverSide?.onDataRequest) {
  //       const exportParams = {
  //         page: 1,
  //         limit: serverSideTable.totalCount, // Get ALL records
  //         ...serverSideTable.filters,
  //         ...(serverSideTable.sorting.field &&
  //           serverSideTable.sorting.direction && {
  //             sortBy: serverSideTable.sorting.field,
  //             sortOrder: serverSideTable.sorting.direction,
  //           }),
  //         ...(serverSideTable.dateRange.field &&
  //           serverSideTable.dateRange.range !== "all" && {
  //             dateField: serverSideTable.dateRange.field,
  //             dateRange: serverSideTable.dateRange.range,
  //             ...(serverSideTable.dateRange.startDate && {
  //               startDate: serverSideTable.dateRange.startDate,
  //             }),
  //             ...(serverSideTable.dateRange.endDate && {
  //               endDate: serverSideTable.dateRange.endDate,
  //             }),
  //           }),
  //       };

  //       // Fetch all filtered data
  //       const response = await serverSide.onDataRequest(exportParams, true);

  //       if (response.success) {
  //         // Convert server data to table rows format
  //         console.log("response data:", response.data);
  //         const allFilteredRows = response.data.map((item, index) => ({
  //           id: index.toString(),
  //           original: item,
  //           getValue: async (columnId: string) => {
  //             const column = columns.find((col) => col.id === columnId);
  //             if (column?.accessorKey) {
  //               return item[column.accessorKey as keyof T];
  //             }
  //             if (column?.accessorFn) {
  //               const result = column.accessorFn(item);
  //               return result instanceof Promise ? await result : result;
  //             }
  //             return "";
  //           },
  //         }));

  //         console.log("Exporting all filtered rows:", allFilteredRows);

  //         await exportRows(
  //           allFilteredRows,
  //           visibleColumns,
  //           "all-filtered",
  //           response.meta.total
  //         );
  //       } else {
  //         throw new Error(
  //           `Failed to fetch data for export: ${response.message}`
  //         );
  //       }
  //     } else {
  //       const rows = table.getFilteredRowModel().rows; // All filtered data
  //       await exportRows(rows, visibleColumns, "all-filtered");
  //     }
  //   } catch (error) {
  //     console.error("Export all filtered error:", error);
  //   } finally {
  //     setIsExporting(false);
  //   }
  // };

  const exportAllFiltered = async () => {
    try {
      const visibleColumns = table
        .getVisibleFlatColumns()
        .filter((col) => col.id !== "select");

      if (isServerSide && serverSide?.onDataRequest) {
        const totalCount = serverSideTable.totalCount;

        // Check if we should use batched export
        if (batchedExport.shouldUseBatchedExport(totalCount)) {
          setShowProgressModal(true);
          batchedExport.resetProgress();

          try {
            // Use batched export for large datasets
            const allData = await batchedExport.exportInBatches(
              totalCount,
              serverSideTable.filters,
              serverSideTable.sorting,
              serverSideTable.dateRange,
              (batchData, batchNumber) => {
                console.log(
                  `Completed batch ${batchNumber}: ${batchData.length} records`
                );
              }
            );

            // Convert to table rows format
            const allFilteredRows = allData.map((item, index) => ({
              id: index.toString(),
              original: item,
              getValue: async (columnId: string) => {
                const column = columns.find((col) => col.id === columnId);
                if (column?.accessorKey) {
                  return item[column.accessorKey as keyof T];
                }
                if (column?.accessorFn) {
                  const result = column.accessorFn(item);
                  return result instanceof Promise ? await result : result;
                }
                return "";
              },
            }));

            // The progress is automatically updated by the hook
            // Just export the rows
            await exportRows(
              allFilteredRows,
              visibleColumns,
              "all-filtered-batched",
              totalCount
            );

            // Progress state is managed by the hook automatically
            console.log("Batched export completed successfully");
          } catch (error) {
            console.error("Batched export error:", error);
            // Error state is also managed by the hook
          }
        } else {
          // Use single request for smaller datasets (existing logic)
          setIsExporting(true);

          const exportParams = {
            page: 1,
            limit: totalCount,
            ...serverSideTable.filters,
            ...(serverSideTable.sorting.field &&
              serverSideTable.sorting.direction && {
                sortBy: serverSideTable.sorting.field,
                sortOrder: serverSideTable.sorting.direction,
              }),
            ...(serverSideTable.dateRange.field &&
              serverSideTable.dateRange.range !== "all" && {
                dateField: serverSideTable.dateRange.field,
                dateRange: serverSideTable.dateRange.range,
                ...(serverSideTable.dateRange.startDate && {
                  startDate: serverSideTable.dateRange.startDate,
                }),
                ...(serverSideTable.dateRange.endDate && {
                  endDate: serverSideTable.dateRange.endDate,
                }),
              }),
          };

          const response = await serverSide.onDataRequest(exportParams, true);

          if (response.success) {
            const allFilteredRows = response.data.map((item, index) => ({
              id: index.toString(),
              original: item,
              getValue: async (columnId: string) => {
                const column = columns.find((col) => col.id === columnId);
                if (column?.accessorKey) {
                  return item[column.accessorKey as keyof T];
                }
                if (column?.accessorFn) {
                  const result = column.accessorFn(item);
                  return result instanceof Promise ? await result : result;
                }
                return "";
              },
            }));

            await exportRows(
              allFilteredRows,
              visibleColumns,
              "all-filtered",
              response.meta.total
            );
          } else {
            throw new Error(
              `Failed to fetch data for export: ${response.message}`
            );
          }
        }
      } else {
        // Client-side export (existing logic)
        const rows = table.getFilteredRowModel().rows;
        await exportRows(rows, visibleColumns, "all-filtered");
      }
    } catch (error) {
      console.error("Export all filtered error:", error);
      // For batched exports, error is handled by the hook
      // For single exports, we can show a simple alert or toast
      let errorMessage = "Unknown error";
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === "string") {
        errorMessage = error;
      }
      if (!batchedExport.progress.isExporting) {
        alert(`Export failed: ${errorMessage}`);
      }
    } finally {
      setIsExporting(false);
    }
  };

  const exportRows = async (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    rows: any,
    visibleColumns: Column<T, unknown>[],
    type: string,
    totalCount?: number
  ) => {
    try {
      // Filter out columns that are excluded from export
      const exportableColumns = visibleColumns.filter((column) => {
        const columnConfig = columns.find((col) => col.id === column.id);
        return !columnConfig?.exportConfig?.excludeFromExport;
      });

      // Build headers - use export header if specified, otherwise use display header
      const headers = exportableColumns.map((column) => {
        const columnConfig = columns.find((col) => col.id === column.id);

        if (columnConfig?.exportConfig?.exportHeader) {
          return columnConfig.exportConfig.exportHeader;
        }

        const header = column.columnDef.header;
        return typeof header === "string" ? header : column.id;
      });

      // Process rows in parallel but maintain order
      const csvRows = await Promise.all(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        rows.map(async (row: any) => {
          // Process all columns for this row in parallel
          const cellValues = await Promise.all(
            exportableColumns.map(async (column) => {
              try {
                const columnConfig = columns.find(
                  (col) => col.id === column.id
                );
                let rawValue;

                // Step 1: Extract the raw value using the most appropriate method
                if (columnConfig?.exportConfig?.exportValueFn) {
                  // Priority 1: Use custom export function if defined
                  rawValue = await Promise.resolve(
                    columnConfig.exportConfig.exportValueFn(row.original)
                  );
                } else if (typeof row.getValue === "function") {
                  // Priority 2: Use row's getValue method
                  const value = row.getValue(column.id);
                  rawValue = value instanceof Promise ? await value : value;
                } else if (row.original && columnConfig?.accessorKey) {
                  // Priority 3: Direct property access
                  rawValue = row.original[columnConfig.accessorKey as keyof T];
                } else if (row.original && columnConfig?.accessorFn) {
                  // Priority 4: Use accessor function
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                  const fnResult = columnConfig.accessorFn(row.original);
                  rawValue =
                    fnResult instanceof Promise ? await fnResult : fnResult;
                } else {
                  rawValue = "";
                }

                // Step 2: Format the value for export
                let exportValue;
                if (columnConfig?.exportConfig?.formatForExport) {
                  exportValue =
                    columnConfig.exportConfig.formatForExport(rawValue);
                } else {
                  exportValue = formatValueForCSV(rawValue);
                }

                return exportValue;
              } catch (error) {
                console.error(
                  `Error processing export value for column ${column.id}:`,
                  error
                );
                return ""; // Return empty string on error
              }
            })
          );

          return cellValues.join(",");
        })
      );

      const csvContent = [headers.join(","), ...csvRows].join("\n");

      console.log("Exporting CSV content:", csvContent);

      // Create and download the file
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);

      const filename = `table-export-${type}-${
        new Date().toISOString().split("T")[0]
      }${totalCount ? `-${totalCount}-records` : ""}.csv`;

      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Export error:", error);
      throw error;
    }
  };

  // Helper function for default CSV formatting
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const formatValueForCSV = (value: any): string => {
    if (value === null || value === undefined) {
      return "";
    }

    let stringValue: string;

    // Handle different data types
    if (
      typeof value === "string" ||
      typeof value === "number" ||
      typeof value === "boolean"
    ) {
      stringValue = String(value);
    } else if (value instanceof Date) {
      stringValue = value.toISOString();
    } else if (typeof value === "object") {
      // For objects, try to extract meaningful text
      if (value.props && typeof value.props.children === "string") {
        // React element with string children
        stringValue = value.props.children;
      } else if (Array.isArray(value)) {
        // Arrays - join with semicolons
        stringValue = value
          .map((item) =>
            typeof item === "object" ? JSON.stringify(item) : String(item)
          )
          .join("; ");
      } else {
        // Generic object - convert to JSON
        stringValue = JSON.stringify(value);
      }
    } else {
      stringValue = String(value);
    }

    // Clean up the string
    stringValue = stringValue
      .replace(/<[^>]*>/g, "") // Remove HTML tags
      .replace(/"/g, '""') // Escape quotes
      .trim();

    // Wrap in quotes if contains special characters
    if (
      stringValue.includes(",") ||
      stringValue.includes("\n") ||
      stringValue.includes('"') ||
      stringValue.includes(";")
    ) {
      return `"${stringValue}"`;
    }

    return stringValue;
  };

  // View management functions
  const saveViewsToStorage = (views: ViewState[]) => {
    if (enableCustomViews && viewStorageKey) {
      localStorage.setItem(viewStorageKey, JSON.stringify(views));
    }
  };

  const loadViewsFromStorage = useCallback((): ViewState[] => {
    if (!enableCustomViews || !viewStorageKey) return [];

    try {
      const stored = localStorage.getItem(viewStorageKey);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error("Error loading views from storage:", error);
      return [];
    }
  }, [enableCustomViews, viewStorageKey]);

  const saveCurrentAsView = (viewName: string, isDefault = false) => {
    const newView: ViewState = {
      id: `view-${Date.now()}`,
      name: viewName,
      columnVisibility: table.getState().columnVisibility,
      pageSize: table.getState().pagination.pageSize,
      isDefault,
    };

    let updatedViews = [...customViews];

    // Remove existing default if setting new default
    if (isDefault) {
      updatedViews = updatedViews.map((v) => ({ ...v, isDefault: false }));
    }

    updatedViews.push(newView);
    setCustomViews(updatedViews);
    saveViewsToStorage(updatedViews);
    setCurrentView(newView);

    if (onViewChange) {
      onViewChange(newView);
    }
  };

  const deleteView = (viewId: string) => {
    const viewToDelete = customViews.find((v) => v.id === viewId);
    if (viewToDelete) {
      setViewToDelete(viewToDelete);
    }
  };

  const confirmDeleteView = () => {
    if (viewToDelete) {
      const updatedViews = customViews.filter((v) => v.id !== viewToDelete.id);
      setCustomViews(updatedViews);
      saveViewsToStorage(updatedViews);

      if (currentView?.id === viewToDelete.id) {
        setCurrentView(null);
        resetToDefault();
      }

      setViewToDelete(null);
    }
  };

  const resetToDefault = () => {
    const defaultVisibility: Record<string, boolean> = {};
    columns.forEach((col) => {
      defaultVisibility[col.id] = true;
    });

    table.setColumnVisibility(defaultVisibility);
    table.setPageSize(initialPageSize);
    setCurrentView(null);
  };

  const handleDateRangeChange = (
    range: string,
    startDate?: string,
    endDate?: string
  ) => {
    setDateRange(range);

    if (isServerSide && dateFilterField) {
      // For server-side, pass the calculated dates directly
      serverSideTable.setDateRangeFilter(
        dateFilterField,
        range,
        startDate,
        endDate
      );
    } else {
      // For client-side, apply the date filtering with calculated dates
      if (range === "all") {
        setFilteredData(data);
      } else if (startDate && endDate) {
        const filtered = data.filter((item: T) => {
          const itemDate = item[dateFilterField as keyof T] as unknown;
          if (!itemDate) return false;

          try {
            const date = new Date(itemDate as string);
            const start = new Date(startDate);
            const end = new Date(endDate);
            return date >= start && date <= end;
          } catch (error) {
            console.error("Date parsing error:", error);
            return false;
          }
        });
        setFilteredData(filtered);
      }
    }
  };

  useEffect(() => {
    if (!isServerSide) {
      // For client-side, start with all data if no date range is selected
      if (dateRange === "all") {
        setFilteredData(data);
      }
      // Note: Specific date range filtering is now handled by handleDateRangeChange
      // When user selects a date range, handleDateRangeChange will be called with calculated dates
    }
  }, [data, isServerSide, dateRange, dateFilterField]);

  // Handle server-side column filter changes
  const handleServerColumnFilter = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (columnId: string, value: any) => {
      if (isServerSide) {
        serverSideTable.setColumnFilter(columnId, value);
      }
    },
    [isServerSide, serverSideTable]
  );

  // Handle server-side sorting
  const handleServerSorting = useCallback(
    (columnId: string, direction: "asc" | "desc" | false) => {
      if (isServerSide) {
        const column = columns.find((col) => col.id === columnId);
        const sortKey = column?.serverSortKey || columnId;

        if (direction === false) {
          serverSideTable.setSorting(null, null);
        } else {
          serverSideTable.setSorting(sortKey, direction);
        }
      }
    },
    [isServerSide, serverSideTable, columns]
  );

  const multiSelectFilterFn = (
    row: Row<T>,
    columnId: string,
    filterValue: string[]
  ) => {
    const cellValue = row.getValue(columnId);

    if (!filterValue || filterValue.length === 0) return true;
    if (!cellValue) return false;

    const cellString = String(cellValue).toLowerCase();

    // Check if cell value matches any of the selected values
    return filterValue.some((value) =>
      cellString.includes(String(value).toLowerCase())
    );
  };

  // Convert our column config to TanStack columns
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const tanstackColumns = useMemo<ColumnDef<T, any>[]>(() => {
    return columns.map((col) => ({
      id: col.id,
      header: col.header,
      accessorKey: col.accessorKey,
      accessorFn: col.accessorFn,
      cell: col.enableEditing
        ? (props: CellContext<T, unknown>) => (
            <EditableCellWithFormatting {...props} columnConfig={col} />
          )
        : col.cell || ((ctx: CellContext<T, unknown>) => ctx.getValue()),
      filterFn:
        col.filterType === "multi-select" ||
        (col.filterType === "select" && col.enableMultiSelect)
          ? ("multiSelect" as FilterFnOption<T>)
          : undefined,
      enableSorting: col.enableSorting ?? false,
      enableColumnFilter: col.enableColumnFilter ?? false,
      enableGlobalFilter: col.enableGlobalFilter ?? false,
      size: col.size,
      meta: {
        columnConfig: col,
        filterType: col.filterType,
        filterOptions: col.filterOptions,
        skeleton: col.skeleton,
        enableEditing: col.enableEditing,
        editType: col.editType,
        editOptions: col.editOptions,
        onCellEdit: col.onCellEdit,
      },
    }));
  }, [columns]);

  const table = useReactTable({
    data: tableData,
    columns: tanstackColumns,
    getCoreRowModel: getCoreRowModel(),
    ...(isServerSide
      ? {}
      : {
          getFilteredRowModel: getFilteredRowModel(),
          getSortedRowModel: getSortedRowModel(),
          ...(enablePagination && {
            getPaginationRowModel: getPaginationRowModel(),
          }),
        }),
    state: {
      ...(isServerSide ? {} : { globalFilter }),
      ...(enablePagination && !isServerSide && { pagination }),
      columnVisibility,
    },
    ...(isServerSide
      ? {}
      : {
          onGlobalFilterChange: setGlobalFilter,
          ...(enablePagination && {
            onPaginationChange: setPagination,
          }),
          globalFilterFn: "includesString",
        }),
    onColumnVisibilityChange: setColumnVisibility,
    // Disable built-in pagination for server-side
    manualPagination: isServerSide,
    manualSorting: isServerSide,
    manualFiltering: isServerSide,
    ...(isServerSide && {
      pageCount: serverSideTable.totalPages,
    }),
    filterFns: {
      multiSelect: multiSelectFilterFn,
    },
  });

  const applyView = useCallback(
    (view: ViewState) => {
      table.setColumnVisibility(view.columnVisibility);

      if (isServerSide) {
        serverSideTable.setPageSize(view.pageSize);
      } else {
        table.setPageSize(view.pageSize);
      }
      setCurrentView(view);

      if (onViewChange) {
        onViewChange(view);
      }
    },
    [table, onViewChange, isServerSide, serverSideTable]
  );

  useEffect(() => {
    if (enableCustomViews) {
      const views = loadViewsFromStorage();
      setCustomViews(views);

      // Apply default view if exists
      const defaultView = views.find((v) => v.isDefault);
      if (defaultView) {
        // Delay application to ensure table is ready
        setTimeout(() => applyView(defaultView), 100);
      }
    }
  }, [enableCustomViews, loadViewsFromStorage]);

  const renderSortIcon = (column: Column<T, unknown>) => {
    if (!column.getCanSort()) return null;

    let sorted;
    if (isServerSide) {
      // For server-side, check our server state
      const columnId = column.id;
      const serverColumn = columns.find((col) => col.id === columnId);
      const sortKey = serverColumn?.serverSortKey || columnId;

      if (serverSideTable.sorting.field === sortKey) {
        sorted = serverSideTable.sorting.direction;
      } else {
        sorted = false;
      }
    } else {
      sorted = column.getIsSorted();
    }

    if (sorted === "asc") {
      return (
        <ChevronUp className={`ml-2 h-4 w-4 ${resolvedTheme.headerText}`} />
      );
    }
    if (sorted === "desc") {
      return (
        <ChevronDown className={`ml-2 h-4 w-4 ${resolvedTheme.headerText}`} />
      );
    }
    return (
      <ChevronsUpDown className={`ml-2 h-4 w-4 ${resolvedTheme.headerText}`} />
    );
  };

  const handleColumnSort = (column: Column<T, unknown>) => {
    if (isServerSide) {
      const columnId = column.id;
      const serverColumn = columns.find((col) => col.id === columnId);
      const sortKey = serverColumn?.serverSortKey || columnId;

      let newDirection: "asc" | "desc" | false = "asc";

      if (serverSideTable.sorting.field === sortKey) {
        if (serverSideTable.sorting.direction === "asc") {
          newDirection = "desc";
        } else if (serverSideTable.sorting.direction === "desc") {
          newDirection = false;
        }
      }

      handleServerSorting(columnId, newDirection);
    } else {
      column.toggleSorting();
    }
  };

  // Custom pagination handlers for server-side
  const handlePageChange = (pageIndex: number) => {
    if (isServerSide) {
      serverSideTable.setPage(pageIndex + 1); // Server pages are 1-based
    } else {
      table.setPageIndex(pageIndex);
    }
  };

  const handlePageSizeChange = (pageSize: number) => {
    if (isServerSide) {
      serverSideTable.setPageSize(pageSize);
    } else {
      table.setPageSize(pageSize);
    }
  };

  // Error display component
  const ErrorDisplay = () => {
    if (!currentError) return null;

    return (
      <div className="flex items-center justify-center py-8">
        <div className="flex items-center space-x-2 text-red-600">
          <AlertCircle className="h-5 w-5" />
          <span>Error: {currentError}</span>
          <Button
            size="sm"
            variant="outline"
            onClick={() => serverSideTable.refresh()}
            className="ml-4"
          >
            Retry
          </Button>
        </div>
      </div>
    );
  };

  const renderExportCSVMenu = () => {
    const totalCount = isServerSide
      ? serverSideTable.totalCount
      : table.getFilteredRowModel().rows.length;
    const willUseBatchedExport =
      isServerSide && batchedExport.shouldUseBatchedExport(totalCount);

    return (
      <DropdownMenuContent className={`rounded-lg ${resolvedTheme.rowEvenBg}`}>
        <DropdownMenuItem
          onClick={exportCurrentPage}
          disabled={isExporting || batchedExport.progress.isExporting}
          className={`rounded-full ${resolvedTheme.rowText} ${resolvedTheme.buttonSecondaryHover}`}
        >
          Export Current Page ({table.getRowModel().rows.length} rows)
        </DropdownMenuItem>

        <DropdownMenuItem
          onClick={exportAllFiltered}
          disabled={isExporting || batchedExport.progress.isExporting}
          className={`rounded-full ${resolvedTheme.rowText} ${resolvedTheme.buttonSecondaryHover}`}
        >
          {isServerSide ? (
            willUseBatchedExport ? (
              <>
                Export All Filtered (Batched - {totalCount.toLocaleString()}{" "}
                rows)
              </>
            ) : (
              `Export All Filtered (${totalCount.toLocaleString()} records)`
            )
          ) : (
            `Export All Filtered (${
              table.getFilteredRowModel().rows.length
            } rows)`
          )}
        </DropdownMenuItem>
      </DropdownMenuContent>
    );
  };

  return (
    <div className="space-y-4">
      {/* Controls Row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          {enableGlobalSearch && !serverSide && (
            <GlobalFilter
              globalFilter={globalFilter}
              setGlobalFilter={setGlobalFilter}
              placeholder={globalSearchPlaceholder}
              theme={resolvedTheme}
            />
          )}
          {enableDateRangeFilter && (
            <DateRangeFilter
              onDateRangeChange={handleDateRangeChange}
              currentRange={
                isServerSide ? serverSideTable.dateRange.range : dateRange
              }
              theme={resolvedTheme}
              serverSide={isServerSide}
              onServerDateRangeChange={serverSideTable.setDateRangeFilter}
              dateField={dateFilterField}
              enableCustomDateRangeFilter={enableCustomDateRangeFilter}
            />
          )}
        </div>
        <div className="flex items-center space-x-4">
          {enableCustomViews && (
            <div
              className={`flex items-center rounded-full ${resolvedTheme.rowEvenBg}`}
            >
              <ViewSelector
                views={customViews}
                currentView={currentView}
                onViewSelect={applyView}
                onDeleteView={deleteView}
                theme={resolvedTheme}
              />
              <SaveViewDialog
                onSaveView={saveCurrentAsView}
                theme={resolvedTheme}
              />
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      className={`rounded-full p-2 cursor-pointer hover:bg-gray-200 transition-colors`}
                      onClick={resetToDefault}
                    >
                      <RotateCw className="h-4 w-4" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent
                    className={`${resolvedTheme.buttonPrimaryBg} ${resolvedTheme.buttonPrimaryText}`}
                  >
                    <p>Reset To Default View</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          )}
          {enableColumnVisibility && (
            <ColumnVisibilityToggle
              table={table as TanStackTable<T>}
              theme={resolvedTheme}
            />
          )}

          {enableCSVExport && (
            <DropdownMenu>
              <DropdownMenuTrigger
                asChild
                className={`rounded-full ${resolvedTheme.rowEvenBg} ${resolvedTheme.rowText} ${resolvedTheme.buttonSecondaryBorder} ${resolvedTheme.buttonSecondaryHover}`}
              >
                <Button variant="outline" size="sm" disabled={isExporting}>
                  <Download className="h-4 w-4" />
                  {isExporting ? "Exporting..." : "Export CSV"}
                  <ChevronDown className="h-4 w-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              {renderExportCSVMenu()}
            </DropdownMenu>
          )}

          {isServerSide && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    className={`inline-flex items-center justify-center whitespace-nowrap rounded-full text-sm font-medium h-9 w-9 cursor-pointer hover:bg-gray-200 transition-colors ${
                      isLoading ? "opacity-50 pointer-events-none" : ""
                    }`}
                    onClick={() => !isLoading && serverSideTable.refresh()}
                    disabled={isLoading}
                  >
                    <RotateCw
                      className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`}
                    />
                  </button>
                </TooltipTrigger>
                <TooltipContent
                  className={`${resolvedTheme.buttonPrimaryBg} ${resolvedTheme.buttonPrimaryText}`}
                >
                  <p>Refresh Data</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      </div>

      {/* Error Display */}
      <ErrorDisplay />

      {/* Table */}
      <div
        className={`rounded-[1rem] ${resolvedTheme.tableShadow} overflow-x-auto`}
      >
        <Table>
          <TableHeader
            className={`${resolvedTheme.headerBg} ${resolvedTheme.headerText}`}
          >
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const meta = header.column.columnDef.meta as ColumnConfig<T>;
                  return (
                    <TableHead
                      key={header.id}
                      className={`pt-6 pb-4`}
                      style={{ width: header.getSize() }}
                    >
                      <div className="space-y-1">
                        {/* Header with sorting */}
                        <div
                          className={`flex items-center font-semibold text-md ${
                            header.column.getCanSort()
                              ? "cursor-pointer select-none"
                              : ""
                          }`}
                          onClick={() =>
                            header.column.getCanSort() &&
                            handleColumnSort(header.column)
                          }
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          {renderSortIcon(header.column)}
                        </div>

                        {/* Column Filter */}
                        {header.column.getCanFilter() && meta?.filterType && (
                          <div className="mt-1">
                            <ColumnFilter
                              column={header.column}
                              filterType={meta.filterType}
                              filterOptions={meta.filterOptions}
                              theme={resolvedTheme}
                              serverSide={isServerSide}
                              debounceMs={serverSide?.debounceMs}
                              onServerFilterChange={handleServerColumnFilter}
                            />
                          </div>
                        )}
                      </div>
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {(isServerSide && serverSideTable.loading) || loading ? (
              // Skeleton rows
              Array.from({ length: skeletonRows }).map((_, rowIndex) => (
                <TableRow
                  key={rowIndex}
                  className={`${resolvedTheme.rowEvenBg}`}
                >
                  {table.getVisibleFlatColumns().map((column, colIndex) => {
                    const meta = column.columnDef.meta as ColumnConfig<T>;
                    return (
                      <TableCell key={colIndex}>
                        <Skeleton
                          className={`h-${meta?.skeleton?.height || "6"} w-${
                            meta?.skeleton?.width || "[200px]"
                          }`}
                        />
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            ) : currentError ? null : table.getRowModel().rows.length === 0 ? ( // Error handle by ErrorDisplay component above
              // Empty state
              <TableRow>
                <TableCell
                  colSpan={table.getVisibleFlatColumns().length}
                  className={`text-center py-16 ${resolvedTheme.rowText}`}
                >
                  <div className="flex flex-col items-center">
                    <svg
                      className={`w-12 h-12 ${resolvedTheme.headerText} mb-4`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <p className={`${resolvedTheme.rowText}`}>{emptyMessage}</p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              table.getRowModel().rows.map((row: any, index) => {
                const rowData = row.original;
                const rowId = getRowId ? getRowId(rowData) : row.index;
                const isExpanded = hasSubRows && expandedItems.has(rowId);
                const subRows =
                  hasSubRows && getSubRows ? getSubRows(rowData) : [];

                return (
                  <React.Fragment key={row.id}>
                    {/* Main row */}
                    <TableRow
                      className={`transition-colors text-md ${
                        onRowClick ? "cursor-pointer" : ""
                      } ${
                        isExpanded
                          ? resolvedTheme.rowOdd
                          : index % 2 === 0
                          ? resolvedTheme.rowEvenBg
                          : resolvedTheme.rowOdd
                      } ${resolvedTheme.rowHover} ${resolvedTheme.rowText}`}
                      onClick={() => {
                        // Handle expansion toggle AND row click
                        if (
                          hasSubRows &&
                          onToggleExpand &&
                          rowId !== undefined
                        ) {
                          onToggleExpand(rowId);
                        }
                        if (onRowClick) {
                          onRowClick(row.original);
                        }
                      }}
                    >
                      {row.getVisibleCells().map((cell: Cell<T, unknown>) => (
                        <TableCell key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </TableCell>
                      ))}
                    </TableRow>

                    {/* Sub-rows */}
                    {hasSubRows &&
                      getSubRows &&
                      isExpanded &&
                      subRows.length > 0 && (
                        <TableRow>
                          <TableCell
                            colSpan={table.getVisibleFlatColumns().length}
                            className="p-0"
                          >
                            <SubRowsTable
                              subRows={subRows}
                              subColumns={subRowsColumns}
                              parentIndex={index}
                              theme={resolvedTheme}
                            />
                          </TableCell>
                        </TableRow>
                      )}
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {enablePagination &&
        (isServerSide ? (
          <ServerSidePagination
            currentPage={serverSideTable.currentPage}
            pageSize={serverSideTable.pageSize}
            totalCount={
              serverSideTable.loading ? 0 : serverSideTable.totalCount
            }
            totalPages={
              serverSideTable.loading ? 1 : serverSideTable.totalPages
            }
            hasNextPage={
              serverSideTable.loading ? false : serverSideTable.hasNextPage
            }
            hasPreviousPage={
              serverSideTable.loading ? false : serverSideTable.hasPreviousPage
            }
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            pageSizeOptions={pageSizeOptions}
            loading={serverSideTable.loading}
            theme={resolvedTheme}
          />
        ) : (
          !loading && ( // Only hide for client-side when loading
            <TablePagination
              table={table as TanStackTable<T>}
              pageSizeOptions={pageSizeOptions}
              theme={resolvedTheme}
            />
          )
        ))}

      <ExportProgressModal
        progress={batchedExport.progress}
        onCancel={batchedExport.cancelExport}
        onClose={() => {
          setShowProgressModal(false);
          batchedExport.resetProgress();
        }}
        isOpen={showProgressModal}
      />

      <DeleteViewDialog
        viewToDelete={viewToDelete}
        onConfirm={confirmDeleteView}
        onCancel={() => setViewToDelete(null)}
      />
    </div>
  );
}

const ServerSidePagination = ({
  currentPage,
  pageSize,
  totalCount,
  totalPages,
  hasNextPage,
  hasPreviousPage,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions,
  loading = false,
  theme,
}: {
  currentPage: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  pageSizeOptions: number[];
  loading: boolean;
  theme: TableTheme;
}) => {
  return (
    <div className="flex items-center justify-between px-2 py-4">
      <div className="flex items-center space-x-2">
        <p className={`text-sm font-medium ${theme.rowText}`}>Rows per page</p>
        <Select
          value={`${pageSize}`}
          onValueChange={(value) => onPageSizeChange(Number(value))}
          disabled={loading}
        >
          <SelectTrigger
            className={`h-8 w-[70px] rounded-full ${theme.rowEvenBg} ${
              theme.rowText
            } ${theme.buttonSecondaryBorder} ${theme.buttonSecondaryHover} ${
              loading ? "opacity-50" : ""
            }`}
          >
            <SelectValue placeholder={pageSize} />
          </SelectTrigger>
          <SelectContent
            side="top"
            className={`rounded-[1rem] ${theme.rowEvenBg}`}
          >
            {pageSizeOptions.map((size) => (
              <SelectItem
                key={size}
                value={`${size}`}
                className={`rounded-[1rem] ${theme.rowText} ${theme.buttonSecondaryHover}`}
              >
                {size}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center space-x-6 lg:space-x-8">
        <div className="flex items-center space-x-2">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  className={`hidden h-8 w-8 p-0 lg:flex items-center justify-center rounded-full cursor-pointer transition-colors hover:bg-gray-200 ${
                    !hasPreviousPage || loading
                      ? "opacity-50 pointer-events-none"
                      : ""
                  }`}
                  onClick={() => !loading && hasPreviousPage && onPageChange(0)}
                  disabled={!hasPreviousPage || loading}
                >
                  <ChevronsLeft className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent
                className={`${theme.buttonPrimaryBg} ${theme.buttonPrimaryText}`}
              >
                <p>Go to first page</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  className={`h-8 w-8 p-0 flex items-center justify-center rounded-full cursor-pointer transition-colors hover:bg-gray-200 ${
                    !hasPreviousPage || loading
                      ? "opacity-50 pointer-events-none"
                      : ""
                  }`}
                  onClick={() =>
                    !loading && hasPreviousPage && onPageChange(currentPage - 2)
                  }
                  disabled={!hasPreviousPage || loading}
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent
                className={`${theme.buttonPrimaryBg} ${theme.buttonPrimaryText}`}
              >
                <p>Go to previous page</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  className={`h-8 w-8 p-0 flex items-center justify-center rounded-full cursor-pointer transition-colors hover:bg-gray-200 ${
                    !hasNextPage || loading
                      ? "opacity-50 pointer-events-none"
                      : ""
                  }`}
                  onClick={() =>
                    !loading && hasNextPage && onPageChange(currentPage)
                  }
                  disabled={!hasNextPage || loading}
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent
                className={`${theme.buttonPrimaryBg} ${theme.buttonPrimaryText}`}
              >
                <p>Go to next page</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  className={`hidden h-8 w-8 p-0 lg:flex items-center justify-center rounded-full cursor-pointer transition-colors hover:bg-gray-200 ${
                    !hasNextPage || loading
                      ? "opacity-50 pointer-events-none"
                      : ""
                  }`}
                  onClick={() =>
                    !loading && hasNextPage && onPageChange(totalPages - 1)
                  }
                  disabled={!hasNextPage || loading}
                >
                  <ChevronsRight className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent
                className={`${theme.buttonPrimaryBg} ${theme.buttonPrimaryText}`}
              >
                <p>Go to last page</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <div
          className={`flex w-[100px] items-center justify-center text-sm font-medium ${theme.rowText}`}
        >
          {loading ? (
            <span className="animate-pulse">Loading...</span>
          ) : (
            `Page ${currentPage} of ${totalPages}`
          )}
        </div>
        <div className={`text-sm ${theme.rowText}`}>
          {loading ? (
            <span className="animate-pulse">Loading...</span>
          ) : (
            `${totalCount} total entries`
          )}
        </div>
      </div>
    </div>
  );
};
